import { createContext, useContext } from 'react';
import type { FilterPanelControls } from '@/features/common/FilterPanel/types/FilterPanelControls';

/**
 * Which of a panel's pop-up controls a click on `name` leaves open: that one, or none when it was
 * the open one. Only one list of a row can sensibly be open at a time — opening a second closes the
 * first, the way the events calendar's and /stiri's rows always behaved.
 */
export function nextOpenControl(open: string | null, name: string): string | null {
  return open === name ? null : name;
}

/** Provided by FilterPanel; read by its fields through useFilterPanelControl. */
export const FilterPanelContext = createContext<FilterPanelControls | null>(null);

/**
 * The isOpen/onToggle/onClose a controlled pop-up (Dropdown, DateRangeFilter) takes, for the field
 * called `name` in the enclosing FilterPanel. A new kind of field joins the panel's one-open-at-a-
 * time rule through this rather than through props the page has to wire by hand.
 */
export function useFilterPanelControl(name: string) {
  const controls = useContext(FilterPanelContext);
  if (!controls) throw new Error(`Filter field "${name}" must sit inside a <FilterPanel>.`);
  return {
    isOpen: controls.isOpen(name),
    onToggle: () => controls.toggle(name),
    onClose: controls.close,
  };
}
